import type {
  CustomPlanOffer,
  Plan,
  PlanFeatureFlags,
} from "@circle-react/types";
import type { CustomPlanPricing } from "@circle-react/types/CurrentPlan";
import { useQuery } from "react-query";

import { planApi } from "@/react/api/planApi";
import { usePriceTestingEnabled } from "@/react/hooks/experiments/usePriceTestingEnabled";

interface UseFetchPlansProps {
  billing_currency: string | null;
  billing_cycle: string | null;
  locale: string | null;
}

interface UseQueryFetchPlans {
  compare_table_plan_features?: Record<string, PlanFeatureFlags>;
  custom_plan_offer?: CustomPlanOffer;
  custom_plan_offer_plan?: CustomPlanPricing;
  isRefetchingPlans: boolean;
  plans: Plan[];
}

const PLANS_CACHE_TIME = 1000 * 60 * 10; // 10 minutes
const PLANS_STALE_TIME = 1000 * 60; // 1 minute

interface UseFetchPlansResult extends UseQueryFetchPlans {
  compareTablePlanFeatures?: Record<string, PlanFeatureFlags>;
  customPlanOffer?: CustomPlanOffer;
  customPlanOfferPlan?: CustomPlanPricing;
  errorFetchingPlans: any; // [TODO][BillingReactification][AfterRelease] Create error type
  isPlansLoading: boolean;
  isRefetchingPlans: boolean;
  refetchPlans: any;
}
export const useFetchPlans = ({
  billing_cycle,
  billing_currency,
  locale,
}: UseFetchPlansProps): UseFetchPlansResult => {
  const isPriceTestingEnabled = usePriceTestingEnabled();

  const {
    data,
    isInitialLoading: isPlansLoading,
    error: errorFetchingPlans,
    refetch: refetchPlans,
    isRefetching: isRefetchingPlans,
  } = useQuery<UseQueryFetchPlans, Error>({
    queryKey: [
      "plans",
      billing_cycle,
      billing_currency,
      locale,
      isPriceTestingEnabled,
    ],
    queryFn: () =>
      planApi.fetchPlans({
        billing_cycle,
        billing_currency,
        locale,
        isPriceTestingEnabled,
      }),
    enabled: !!billing_cycle,
    gcTime: PLANS_CACHE_TIME,
    staleTime: PLANS_STALE_TIME,
  });

  const auxiliaryBillingCycle = billing_cycle === "month" ? "year" : "month";
  useQuery<UseQueryFetchPlans, Error>({
    queryKey: [
      "plans",
      auxiliaryBillingCycle,
      billing_currency,
      locale,
      isPriceTestingEnabled,
    ],
    queryFn: () =>
      planApi.fetchPlans({
        billing_cycle: auxiliaryBillingCycle,
        billing_currency,
        locale,
        isPriceTestingEnabled,
      }),
    enabled: !!billing_cycle,
    gcTime: PLANS_CACHE_TIME,
    staleTime: PLANS_STALE_TIME,
  });

  const plans = data?.plans ?? [];

  return {
    plans,
    isPlansLoading,
    errorFetchingPlans,
    refetchPlans,
    isRefetchingPlans,
    compareTablePlanFeatures: data?.compare_table_plan_features ?? {},
    customPlanOffer: data?.custom_plan_offer,
    customPlanOfferPlan: data?.custom_plan_offer_plan,
  };
};
